import type BackOfficeConnector from "../data/backOfficeConnector";
import type { IPagination } from "../data/types";
import type EventBus from "../services/eventBus";
import RepositoryBase from "./repositoryBase";
import type { ICreateRepository } from "./types";
import CurrentCreditExposure from "../entities/currentCreditExposure";
import type CreditExposuresFilter from "../entities/filters/creditExposuresFilter";
import { serializeFilters } from "../utils/filters";
import cloneDeep from "lodash/cloneDeep";
import type CreateCreditExposuresRequest from "../entities/createCreditExposuresRequest";
import type UpdateCreditExposuresRequest from "../entities/updateCreditExposuresRequest";
import CreditExposureResponse from "entities/creditExposureResponse";
import CreditExposureDetailResponse from "entities/creditExposureDetailResponse";

export default class CreditExposureRepository
  extends RepositoryBase<BackOfficeConnector>
  implements ICreateRepository<CreditExposureDetailResponse>
{
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  getCurrentCreditExposure(distributorId: string) {
    return this.callApi(api =>
      api.all("call_center").one("distributors", distributorId).all("current_credit_exposure").getEntity(CurrentCreditExposure)
    );
  }

  getAll(distributorId: string, pagination: IPagination, filter: CreditExposuresFilter) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("distributors", distributorId)
          .all("credit_exposure_search")
          .postPagedEntities<CreditExposureResponse>(
            { ...serializeFilters(filter, false), ...pagination },
            CreditExposureResponse
          ),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  get(distributorId: string, creditExposureId: string) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("distributors", distributorId)
          .one("credit_exposures", creditExposureId)
          .getEntity<CreditExposureDetailResponse>(CreditExposureDetailResponse),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  create(creditExposure: CreateCreditExposuresRequest, distributorId: string) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("distributors", distributorId)
          .all("credit_exposures")
          .postEntity(serializeFilters(cloneDeep(creditExposure), false), CreditExposureDetailResponse),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  update(creditExposure: UpdateCreditExposuresRequest, distributorId: string, creditExposureId: string) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("distributors", distributorId)
          .one("credit_exposures", creditExposureId)
          .putOnly(serializeFilters(creditExposure, false)),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  delete(creditExposure: CreditExposureDetailResponse, distributorId: string) {
    return this.callApi(api =>
      api.all("call_center").one("distributors", distributorId).one("credit_exposures", creditExposure.id).delete(creditExposure)
    );
  }
}
